import type { ReactNode } from 'react';

import type { KeyOfType } from '../../types/KeyOfType';
import { pick } from '../../utils/pick';
import type { TableFilter } from '../filters/TableFilter';

/**
 * List of identifiers usable in OAS renderContent that will be populated by a particular
 * function on frontend
 */
export type NamedCellContentRenderFunction =
  | 'routingMatchList'
  | 'loadBalancingVirtualServersPathRulesConfiguration';

/**
 * Common configuration for all column formats
 */
export type TableColumnConfig = {
  /**
   * Identification of the column in the table, must be unique
   *
   * We need a clear key for every column to save user configuration (width, visibility...).
   * Does not have to be a key to field in data, there are columns in the app composed of multiple fields.
   */
  key: string;
  /**
   * Short title in English. If localization occurs eventually, it will be done somehow aside.
   */
  title: string;
  customFrontendTitle?: ReactNode;
  /**
   * If present, it is shown on the title as tooltip on front-end.
   */
  description?: string;
  width?: number;
  fixedWidth?: boolean;
  filter?: TableFilter | false;
  /**
   * Indicate that fields in this column contain an array of items of its base type
   *
   * When creating a new column that does not support array variant, you should disable it
   * by using `array?: false` in its usage (see e.g. createHostnameColumn).
   *
   * We decided to put this to global config instead of TableColumnFormat* because
   * filters use this information, so internal typing of filters is simpler when
   * every column contains it. However, if this shows to be a source of problems
   * in the future, we are open to reconsider this.
   */
  array?: boolean;
  /** Hidden in UI by default */
  hidden?: boolean;
  /** Pinned to the left in UI by default */
  sticky?: boolean;
  disabledSort?: boolean;
  /** Do not allow selecting this column as the colorized column of a report */
  disabledColorize?: boolean;
  /**
   * Allows complete customization of render on frontend.
   *
   * Untyped here to avoid importing React into utils package.
   *
   * @see TableCellContentRenderFunction type in frontend package
   */
  renderContent?: unknown;
  renderContentNamed?: NamedCellContentRenderFunction;
};

/**
 * Useful when passing table column config to column factory along with other fields
 * to purify the base column config itself.
 */
export const pickTableColumnConfig = pick([
  'array',
  'key',
  'title',
  'customFrontendTitle',
  'description',
  'width',
  'fixedWidth',
  'hidden',
  'disabledSort',
  'disabledColorize',
  'sticky',
  'renderContent',
  'renderContentNamed',
]);

/**
 * Type constraint for columns that have a field of same key in data row
 */
export type TableDataColumnConfig<TRow, TValue> = TableColumnConfig & {
  key: KeyOfType<TRow, TValue>;
};
